/**
 * Notice of the pay-as-you-go minimum for pay-as-you-go accounts (F2). It cannot be closed by a plain
 * close: it stays until the customer presses "I understand", which records the acknowledgement on the
 * server (POST /api/profile/payg-floor-ack, caller only). It hides once the profile carries
 * payg_floor_notice_ack_at or the server confirmed it stored the acknowledgement (stored: true);
 * localStorage is only a fallback cache of that (storage may be blocked, so every access is guarded).
 * If the server accepted the press but could not store it (stored: false, column not there yet) the
 * banner hides for this session only and is shown again next time, so the record gets made.
 * Never shown to free, Pro or organisation accounts.
 */
import { useState } from 'react';
import { helpUrl } from '../lib/legalUrls';
import { useAuthStore, useProfile } from '../store/useAuthStore';
import { supabase } from '../lib/supabase';
import {
  PAYG_FLOOR_ACK_LABEL, PAYG_FLOOR_BANNER_TEXT, PAYG_FLOOR_HELP_ANCHOR, paygFloorBannerKey,
} from '../services/paygFloor';

function cachedAck(userId: string): boolean {
  try {
    return localStorage.getItem(paygFloorBannerKey(userId)) === '1';
  } catch {
    return false;
  }
}

export default function PaygFloorBanner() {
  const profile = useProfile();
  // Which user's acknowledgement happened in this session (covers storage that refuses writes).
  const [ackedFor, setAckedFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!profile || profile.plan !== 'payg') return null;
  if (profile.payg_floor_notice_ack_at) return null;
  if (ackedFor === profile.id || cachedAck(profile.id)) return null;

  const [lead] = PAYG_FLOOR_BANNER_TEXT.split(' Details in Help.');

  const acknowledge = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      const res = await fetch('/api/profile/payg-floor-ack', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        setFailed(true);
        return;
      }
      const body = await res.json().catch(() => null) as { stored?: boolean } | null;
      if (body?.stored === true) {
        // Recorded on the server: reflect that locally so the banner stays hidden after a re-render.
        useAuthStore.setState((s) => ({ profile: s.profile ? { ...s.profile, payg_floor_notice_ack_at: new Date().toISOString() } : s.profile }));
        try { localStorage.setItem(paygFloorBannerKey(profile.id), '1'); } catch { /* cache only */ }
      }
      // stored: false -> nothing was recorded; hide for this session, ask again next time.
      setAckedFor(profile.id);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="status" data-testid="payg-floor-banner" className="max-w-7xl mx-auto mt-2 px-3 py-2 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-800 flex items-start justify-between gap-3">
      <span>
        {lead}{' '}
        <a href={helpUrl(PAYG_FLOOR_HELP_ANCHOR)} target="_blank" rel="noopener noreferrer" className="underline font-medium">Details in Help.</a>
      </span>
      <div className="shrink-0 text-right">
        <button type="button" onClick={acknowledge} disabled={busy} className="text-xs font-semibold underline hover:text-sky-950 disabled:opacity-60">
          {PAYG_FLOOR_ACK_LABEL}
        </button>
        {failed && <p role="alert" className="text-xs text-rose-700 mt-1">Could not save, please try again.</p>}
      </div>
    </div>
  );
}
